import { Section, StateBlock, StatusBadge, SummaryStrip } from "@/components/layout-2/ds";
import { DenseTable, FilterChip, InlineAlert, ObjectBadgeTile, StepList, type StepListItem, type StepStatus } from "@/components/layout-2/ds-settings";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { formatParis } from "@/utils/dialer-datetime";
import { formatNumber } from "@/utils/format-fr";
import { Pause, Play, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { feedingClient } from "./api";
import { formatCount, recordsLabel } from "./counts";
import { describeFilters, describeTree, useFilterDefinitions } from "./filter-chips";
import { RecordsStep } from "./records-step";
import { passName, type RunList } from "./runs";
import { settingsItems } from "./settings-used";
import { STEPS, type StepCode } from "./step-dialog";
import { ExclusionsStep, PaceStep, RulesStep, type StepProps } from "./steps";
import type { FeedingPage, FeedingPageProps, RunRow } from "./types";
import { useLoad } from "./use-load";

const LINK = "text-sm text-blue-700 hover:text-blue-900 hover:underline";
/** Chips shown in the records row before "+N". */
const CHIPS = 3;
/** Server checklist codes each configuration row stands for (the campaign's own readiness is the footer's): the records need a source and its filters. */
const CHECKS: Partial<Record<StepCode | "preview", string[]>> = { records: ["source", "filters"], preview: ["dry_run"] };
const CAMPAIGN_READINESS = "campaign_readiness";

type Open = { step: StepCode; setup: boolean } | null;

/**
 * The Feeding tab of a Dialer campaign: a summary, then the configuration as one checklist whose rows
 * each open the dialog that edits them (saved on its own), then the last passes. While the campaign is
 * a draft, the rows are the steps of the first setup, which chains their dialogs. Details (preview,
 * passes, history) open in large dialogs over this page, from sub-routes of the tab.
 */
export function FeedingSettingsPage({ page, base, send, changeService, refresh }: FeedingPageProps) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [open, setOpen] = useState<Open>(null);
    const [runNowOpen, setRunNowOpen] = useState(false);
    const { campaign, source, exclusions, summary, state, checklist } = page;
    const listView = source.list_view;
    const editable = page.can_edit;
    const definitions = useFilterDefinitions(listView?.object.url);
    const limit = page.limits.max_source_records;

    const check = (code: string) => checklist?.find(item => item.code === code);
    const done = (step: StepCode | "preview") => {
        const codes = CHECKS[step];
        return codes ? codes.every(code => !!check(code)?.done) : false;
    };
    const required = ["records", "preview"] as const;
    const setupDone = required.filter(done).length;
    const nextStep: StepCode | "preview" | null = !checklist ? null : !done("records") ? "records" : !done("preview") ? "preview" : null;

    // The former filters and exclusions pages still open their step dialogs.
    useEffect(() => {
        const step = searchParams.get("exclusions") === "edit" ? "exclusions" : searchParams.get("filters") === "edit" ? "records" : null;
        if (!step) return;
        setSearchParams({}, { replace: true });
        if (editable) setOpen({ step, setup: false });
    }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    const openPreview = () => navigate(`${base}/preview`);
    const continueSetup = () => {
        if (nextStep === "preview") openPreview();
        else if (nextStep) setOpen({ step: nextStep, setup: true });
    };
    /** After a step of the first setup: the next one, then the preview. The records cannot be skipped. */
    const advance = (from: StepCode) => {
        const following = STEPS[STEPS.indexOf(from) + 1];
        if (following) setOpen({ step: following, setup: true });
        else {
            setOpen(null);
            openPreview();
        }
    };

    const runNow = async () => {
        setRunNowOpen(false);
        const queued = await send(feedingClient(campaign.code, "run-now/").post({}).json<FeedingPage>(), "Pass started — it appears in the activity once done");
        if (queued) window.setTimeout(refresh, 10_000);
    };

    /* ----- rows ----- */
    const status = (step: StepCode | "preview"): StepStatus | undefined => {
        if (!checklist) return undefined;
        if (step === nextStep) return "next";
        if (CHECKS[step]) return done(step) ? "done" : "todo";
        return "optional";
    };
    const edit = (step: StepCode, label: string, disabled = false) =>
        editable && (
            <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => setOpen({ step, setup: false })} data-testid={`edit-${step}`}>
                {label}
            </Button>
        );

    const chips = listView ? (listView.filter_tree ? describeTree(listView.filter_tree) : describeFilters(listView.filters, definitions)) : [];
    const used = settingsItems({ ...page.config.values, list_view_name: "", filters: {}, filter_logic: null, exclusions: [] }, page.config.fields);
    const pace = used.find(item => item.label === "Run")?.value;
    const order = used.find(item => item.label === "Order")?.value;
    const rules = used.find(item => item.label === "Rules")?.value;
    const objectLabel = source.objects.find(item => item.type === source.object_type)?.label ?? "";

    const items: StepListItem[] = [
        {
            code: "records",
            title: "Records to call",
            status: status("records"),
            summary: !listView ? (
                <span data-testid="empty-listview">Not chosen yet: the object, and the filters a record must match to become a card.</span>
            ) : (
                <span className="flex flex-col gap-1" data-testid="records-summary">
                    <span className="flex flex-wrap items-center gap-1.5">
                        <ObjectBadgeTile icon={listView.object.icon} color={listView.object.color} size="xs" />
                        <span className="text-black">{listView.object.label}</span>
                        {chips.length === 0 ? (
                            <span className="text-amber-800" data-testid="no-filter">
                                · No filter: every {objectLabel.toLowerCase()} record can become a card
                            </span>
                        ) : (
                            <span className="flex flex-wrap gap-1" data-testid="filter-summary">
                                {chips.slice(0, CHIPS).map((chip, index) => (
                                    <FilterChip key={index} label={chip.label} value={chip.value} />
                                ))}
                                {chips.length > CHIPS && <FilterChip label={`+${chips.length - CHIPS}`} />}
                            </span>
                        )}
                        {listView.matching_records !== null && <span>· {recordsLabel(listView.matching_records, limit)}</span>}
                    </span>
                    {listView.origin.kind === "copy" && (
                        <span className="text-xs text-gray-500">
                            Started from “{listView.origin.from_name}” on {formatParis(listView.origin.at, "dd/MM")}
                        </span>
                    )}
                </span>
            ),
            action: edit("records", listView ? "Edit" : "Choose")
        },
        { code: "pace", title: "Pace", status: status("pace"), summary: <span data-testid="pace-summary">{[pace, order].filter(Boolean).join(" · ")}</span>, action: edit("pace", "Edit") },
        { code: "rules", title: "Rules", status: status("rules"), summary: <span data-testid="rules-summary">{rules}</span>, action: edit("rules", "Edit") },
        {
            code: "exclusions",
            title: "Exclusions",
            status: status("exclusions"),
            summary: (
                <span data-testid="exclusions-summary">
                    {exclusions.length === 0 ? "None: every matching record can be called." : `${exclusions.length} ListView${exclusions.length > 1 ? "s" : ""} · ${exclusions.map(view => view.name).join(", ")}`}
                </span>
            ),
            action: edit("exclusions", exclusions.length ? "Manage" : "Add")
        },
        {
            code: "preview",
            title: "Preview",
            status: status("preview"),
            summary: checklist
                ? done("preview")
                    ? "Up to date with the current settings."
                    : "See which records a pass would turn into cards, without creating any."
                : "What the next pass would do now, without creating anything.",
            action: editable && (
                <Button type="button" variant="outline" size="sm" disabled={!listView} onClick={openPreview} data-testid="open-dry-run">
                    Run the preview
                </Button>
            )
        }
    ];

    const readiness = check(CAMPAIGN_READINESS);
    const putInService = summary.put_in_service;
    const footer = checklist && editable && (
        <>
            <span className="text-gray-600" data-testid="put-in-service-hint">
                {readiness && !readiness.done ? readiness.hint : (putInService.hint ?? "Every step is done: the campaign can be put in service.")}
            </span>
            <Button type="button" variant="outline" size="sm" disabled={!putInService.enabled} onClick={() => changeService("activate")} data-testid="activate">
                <Play className="mr-1 h-4 w-4" /> {putInService.label}
            </Button>
        </>
    );

    const stepProps = (step: StepCode): StepProps => ({
        page,
        send,
        onClose: () => setOpen(null),
        context: { setup: open?.setup ? { index: STEPS.indexOf(step) + 1, total: STEPS.length } : null },
        onDone: () => (open?.setup ? advance(step) : setOpen(null))
    });

    return (
        <div className="flex flex-col gap-4">
            {checklist && editable && nextStep && (
                <InlineAlert
                    type="info"
                    title={
                        <span data-testid="setup-progress">
                            First setup · {setupDone} of {required.length} steps done
                        </span>
                    }
                    action={
                        <Button variant="outline" size="sm" onClick={continueSetup} data-testid="continue-setup">
                            Continue setup
                        </Button>
                    }
                >
                    <div className="mt-1 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-blue-100" aria-hidden>
                        <div className="h-full bg-denim-blue" style={{ width: `${(100 * setupDone) / required.length}%` }} />
                    </div>
                </InlineAlert>
            )}

            <SummaryStrip
                status={page.status}
                note={
                    <SimpleTooltip message="Feeding follows the campaign: pausing the campaign suspends injection, resuming it restarts the passes.">
                        <span className="cursor-help underline decoration-dotted">follows the campaign</span>
                    </SimpleTooltip>
                }
                items={[
                    { label: "Last pass", value: summary.last_run?.label, href: summary.last_run ? `${base}/runs/${summary.last_run.id}` : undefined },
                    { label: "Next pass", value: summary.next_run_at ? formatParis(summary.next_run_at) : state === "suspended" ? "Suspended" : null },
                    { label: "Records matching now", value: summary.matching_records === null ? null : formatCount(summary.matching_records, limit) }
                ]}
                actions={
                    editable &&
                    (summary.can_pause ? (
                        <>
                            {summary.run_now && (
                                <SimpleTooltip asChild message={summary.run_now.hint ?? "Run a real pass now, with the saved settings"}>
                                    <span>
                                        <Button variant="outline" size="sm" disabled={!summary.run_now.enabled} onClick={() => setRunNowOpen(true)} data-testid="run-now">
                                            <Zap className="mr-1 h-4 w-4" /> Run a pass now
                                        </Button>
                                    </span>
                                </SimpleTooltip>
                            )}
                            <Button variant="outline" size="sm" onClick={() => changeService("pause")} data-testid="pause">
                                <Pause className="mr-1 h-4 w-4" /> Pause the campaign
                            </Button>
                        </>
                    ) : (
                        // A draft goes live from the end of its checklist; a paused campaign resumes from here.
                        !checklist && (
                            <SimpleTooltip asChild message={putInService.hint ?? putInService.label}>
                                <span>
                                    <Button variant="outline" size="sm" disabled={!putInService.enabled} onClick={() => changeService("activate")} data-testid="activate">
                                        <Play className="mr-1 h-4 w-4" /> {putInService.label}
                                    </Button>
                                </span>
                            </SimpleTooltip>
                        )
                    ))
                }
            />

            {page.alerts.map((alert, index) => (
                <InlineAlert
                    key={index}
                    type={alert.type}
                    title={alert.title}
                    action={
                        alert.action && (
                            <Link to={`${base}/${alert.action.href}`} className={LINK}>
                                {alert.action.label}
                            </Link>
                        )
                    }
                >
                    {alert.text && <span data-testid="feeding-alert">{alert.text}</span>}
                </InlineAlert>
            ))}

            <Section icon="ListChecks" title="Configuration" className="mt-0">
                <div data-testid="readiness">
                    <StepList items={items} footer={footer || undefined} />
                </div>
            </Section>

            <Activity page={page} base={base} />

            {open?.step === "records" && <RecordsStep {...stepProps("records")} />}
            {open?.step === "pace" && <PaceStep {...stepProps("pace")} />}
            {open?.step === "rules" && <RulesStep {...stepProps("rules")} />}
            {open?.step === "exclusions" && <ExclusionsStep {...stepProps("exclusions")} />}

            <AlertDialog open={runNowOpen} onOpenChange={setRunNowOpen}>
                <AlertDialogContent data-testid="run-now-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Run a pass now?</AlertDialogTitle>
                        <AlertDialogDescription>
                            A real pass, with the saved settings: it injects up to {formatNumber(page.config.values.cap_per_pass)} records as cards that will be called. The next scheduled pass counts from this one.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={runNow} data-testid="run-now-confirm">
                            Run the pass
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

/** Number of passes the Activity section shows; the rest is in « All passes ». */
const RECENT = 5;

/** The last passes, each opening its detail, with the links to every pass and to the change history. */
function Activity({ page, base }: { page: FeedingPage; base: string }) {
    // Read again whenever the page is: a preview (a dry run) or a pass by hand refreshes it, a pass of the schedule too.
    const { data, error, loading, reload } = useLoad<RunList>(() => feedingClient(page.campaign.code, "runs/").query({ page: 1 }).get().json<RunList>(), [page.campaign.code, page], "Unable to load the passes.");
    const rows = (data?.results ?? []).slice(0, RECENT);
    return (
        <Section
            icon="History"
            title="Activity"
            actions={
                <>
                    <Link to={`${base}/runs`} className={LINK} data-testid="all-runs">
                        All passes
                    </Link>
                    <Link to={`${base}/history`} className={LINK} data-testid="change-history">
                        Change history
                    </Link>
                </>
            }
        >
            {error || loading ? (
                <StateBlock kind={error ? "error" : "loading"} message={error ?? undefined} onRetry={reload} />
            ) : rows.length === 0 ? (
                <div data-testid="activity-empty">
                    <StateBlock message="No pass yet. Passes show here once the campaign is in service, or after a preview." />
                </div>
            ) : (
                <div data-testid="activity">
                    <DenseTable<RunRow>
                        rows={rows}
                        itemToId={run => String(run.id)}
                        primaryColumn="pass"
                        columns={[
                            {
                                name: "Pass",
                                key: "pass",
                                size: 150,
                                cellFn: run => (
                                    <Link to={`${base}/runs/${run.id}`} className="text-blue-700 hover:text-blue-900 hover:underline" data-testid={`activity-run-${run.id}`}>
                                        {formatParis(run.started_at)}
                                    </Link>
                                )
                            },
                            { name: "Type", key: "type", size: 120, cellFn: run => passName(run) },
                            { name: "Injected", key: "injected", size: 80, cellClassName: "text-right", cellFn: run => <span className="tabular-nums">{formatNumber(run.counters.injected)}</span> },
                            { name: "Ignored", key: "ignored", size: 80, cellClassName: "text-right", cellFn: run => <span className="tabular-nums">{formatNumber(run.counters.ignored)}</span> },
                            { name: "Status", key: "status", size: 110, cellFn: run => <StatusBadge value={run.status} /> }
                        ]}
                    />
                </div>
            )}
        </Section>
    );
}
